import { useState, useEffect, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import api from '@/lib/api';
import { Search, Plus, Check, X, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

const norm = (s) => (s || '').trim().toLowerCase();

function similar(a, b) {
  const x = norm(a); const y = norm(b);
  if (!x || !y) return false;
  if (x === y) return true;
  if (x.length >= 4 && y.length >= 4 && (x.includes(y) || y.includes(x))) return true;
  return false;
}

/**
 * Searchable dropdown for the Material List master.
 * Suggests by usage_count first (popularity-based ranking).
 * Inline-create with soft duplicate warning.
 */
export default function MaterialSelect({
  value,
  onChange,
  projectType,
  placeholder = 'Search or select material',
  testId = 'material-select',
  autoFocus = false
}) {
  const [materials, setMaterials] = useState([]);
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState('');
  const [debounced, setDebounced] = useState('');
  const [open, setOpen] = useState(false);
  const [duplicate, setDuplicate] = useState(null);
  const [pos, setPos] = useState(null);
  const wrapRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (!open) { setPos(null); return; }
    const update = () => {
      if (!inputRef.current) return;
      const r = inputRef.current.getBoundingClientRect();
      setPos({ top: r.bottom + 4, left: r.left, width: r.width });
    };
    update();
    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
    };
  }, [open]);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    api.get('/materials')
      .then(res => { if (mounted) setMaterials(Array.isArray(res.data) ? res.data : []); })
      .catch(() => {})
      .finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, []);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(query), 200);
    return () => clearTimeout(t);
  }, [query]);

  useEffect(() => {
    const onClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  useEffect(() => { if (autoFocus && inputRef.current) inputRef.current.focus(); }, [autoFocus]);

  const filtered = useMemo(() => {
    const q = norm(debounced);
    let list = materials;
    if (q) list = list.filter(m => norm(m.item_name).includes(q));
    // Already pre-sorted by usage_count desc on backend.
    if (projectType && !q) {
      list = [...list].sort((a, b) => {
        const ua = (a.usage_by_type || {})[projectType] || 0;
        const ub = (b.usage_by_type || {})[projectType] || 0;
        if (ua !== ub) return ub - ua;
        return (b.usage_count || 0) - (a.usage_count || 0);
      });
    }
    return list;
  }, [materials, debounced, projectType]);

  const exact = useMemo(
    () => materials.some(m => norm(m.item_name) === norm(query)),
    [materials, query]
  );

  // Proactive duplicate detection: when the user types a name similar (but not
  // exact) to an existing material, surface the "Possible duplicate" panel so
  // they can pick the existing one before forcing a duplicate creation.
  useEffect(() => {
    if (!query.trim()) { setDuplicate(null); return; }
    if (exact) { setDuplicate(null); return; }
    const dup = materials.find(m => similar(m.item_name, query));
    setDuplicate(dup || null);
  }, [debounced, exact, materials, query]);

  const select = (m) => {
    onChange(m.item_name, m);
    setQuery('');
    setOpen(false);
  };

  const clear = (e) => { e.stopPropagation(); onChange('', null); setQuery(''); setOpen(false); };

  const createInline = async () => {
    const name = query.trim();
    if (!name) return;
    // If a duplicate is already surfaced, the user must explicitly resolve via
    // Use Existing / Create Anyway in the panel — abort silently.
    if (duplicate) return;
    try {
      const res = await api.post('/materials', { item_name: name });
      const m = res.data;
      setMaterials(prev => [...prev, { ...m, usage_count: 0 }]);
      onChange(m.item_name, m);
      toast.success('Material created and selected');
      setOpen(false); setQuery(''); setDuplicate(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to create material');
    }
  };

  const useDuplicate = () => {
    if (!duplicate) return;
    onChange(duplicate.item_name, duplicate);
    toast.success(`Selected existing "${duplicate.item_name}"`);
    setDuplicate(null); setOpen(false); setQuery('');
  };

  const forceCreate = async () => {
    const name = query.trim();
    if (!name) return;
    try {
      const res = await api.post('/materials', { item_name: name });
      const m = res.data;
      setMaterials(prev => [...prev, { ...m, usage_count: 0 }]);
      onChange(m.item_name, m);
      toast.success('New material created');
      setDuplicate(null); setOpen(false); setQuery('');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to create material');
    }
  };

  return (
    <div ref={wrapRef} className="relative" data-testid={testId}>
      <div className="relative">
        <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6B7280] pointer-events-none" />
        <input
          ref={inputRef}
          type="text"
          value={open ? query : (value || '')}
          onChange={e => { setQuery(e.target.value); setOpen(true); setDuplicate(null); }}
          onFocus={() => { setQuery(''); setOpen(true); }}
          placeholder={value ? value : placeholder}
          className="w-full h-9 pl-9 pr-9 rounded-lg border border-[#D4D0C8] bg-white text-xs focus:outline-none focus:border-[#3A8C8C] transition-colors"
          data-testid={`${testId}-input`}
          autoComplete="off"
        />
        {value && !open && (
          <button type="button" onClick={clear}
            className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded-full hover:bg-[#E8E6E1]"
            data-testid={`${testId}-clear`}>
            <X size={11} className="text-[#6B7280]" />
          </button>
        )}
      </div>

      {open && pos && createPortal(
        <div className="fixed bg-white border border-[#E8E6E1] rounded-xl shadow-lg max-h-64 overflow-auto"
          style={{ top: pos.top, left: pos.left, width: pos.width, zIndex: 9999 }}
          data-testid={`${testId}-dropdown`}
          onMouseDown={(e) => e.stopPropagation()}>
          {duplicate ? (
            <div className="p-3" data-testid={`${testId}-duplicate-warning`}>
              <div className="flex items-start gap-2">
                <AlertCircle size={13} className="text-[#C67D5B] mt-0.5 shrink-0" />
                <div className="flex-1">
                  <p className="text-[11px] font-semibold text-[#C67D5B]">Possible duplicate</p>
                  <p className="text-[10px] text-[#6B7280] mt-0.5">&ldquo;{duplicate.item_name}&rdquo; already exists.</p>
                  <div className="flex gap-2 mt-2">
                    <button type="button" onClick={useDuplicate}
                      className="text-[10px] text-[#3A8C8C] underline hover:text-[#2E7A7A]"
                      data-testid={`${testId}-use-existing`}>
                      Use existing
                    </button>
                    <button type="button" onClick={forceCreate}
                      className="text-[10px] text-[#6B7280] underline hover:text-[#2C2C2E]"
                      data-testid={`${testId}-create-anyway`}>
                      Create anyway
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <>
              {loading && <div className="px-3 py-3 text-xs text-[#6B7280]">Loading materials…</div>}
              {!loading && !query.trim() && filtered.length === 0 && (
                <div className="px-3 py-3 text-xs text-[#6B7280]">Start typing to search materials</div>
              )}
              {!loading && query.trim() && filtered.length === 0 && (
                <div className="px-3 py-3 text-xs text-[#6B7280]">No materials found</div>
              )}
              {!loading && filtered.slice(0, 50).map(m => (
                <button key={m.id} type="button" onClick={() => select(m)}
                  className="w-full text-left px-3 py-2 hover:bg-[#FAFAF8] flex items-center gap-2 transition-colors"
                  data-testid={`${testId}-option-${m.id}`}>
                  {value === m.item_name
                    ? <Check size={11} className="text-[#3A8C8C] shrink-0" />
                    : <span className="w-3 shrink-0" />}
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium text-[#2C2C2E] truncate">{m.item_name}</p>
                    {(m.usage_count || 0) > 0 && (
                      <p className="text-[9px] text-[#6B7280]">Used {m.usage_count}× across projects</p>
                    )}
                  </div>
                </button>
              ))}
              {query.trim() && !exact && (
                <button type="button" onClick={createInline}
                  className="w-full text-left px-3 py-2 border-t border-[#E8E6E1] bg-[#3A8C8C]/5 hover:bg-[#3A8C8C]/10 flex items-center gap-2 transition-colors"
                  data-testid={`${testId}-add-new`}>
                  <Plus size={12} className="text-[#3A8C8C] shrink-0" />
                  <span className="text-xs text-[#3A8C8C] font-medium truncate">
                    Add &ldquo;<span className="font-semibold">{query}</span>&rdquo; as new material
                  </span>
                </button>
              )}
            </>
          )}
        </div>,
        document.body
      )}
    </div>
  );
}
